import { useQuery } from 'react-query';
import { 
  getPredictionsByProduct, 
  getLastSixMonthsPredictions,
  getConsolidatedIndexes,
  getConsolidatedPredictions
} from "./services/PredictionsService";
import { CACHE_TTL_MS } from './ttl.cache';

export const usePredictions = (productId, options = {}) => {
  const { 
    shouldFetchPredictionByProduct = false,
    shouldFetchConsolidatedIndexes = false,
    shouldFetchLastSixMonthsPredictions = false,
    shouldFetchConsolidatedPredictions = false
  } = options || {}

  const productPredictionsData = useQuery({
    queryKey: ["productPredictions", productId],
    queryFn: async () => {
      if (!productId) throw new Error("ID do produto é obrigatório");
      
      const result = await getPredictionsByProduct(productId);

      const monthsInfo = result?.monthsInformation ?? {};
      
      return {
        allPredictions: result?.allPredictionsByProductId ?? {},
        latestForecastPredictions: result?.predictionsByProductIdByLatestForecast ?? {},
        monthSequences: {
          allMonths: monthsInfo.historicalAndCurrentMonthSequence ?? [],
          allHistoricMonths: monthsInfo.historicalMonthSequence ?? [],
        },
        confidenceIntervalMap: result?.confidenceIntervalMap ?? {},
        confidenceInterval: result?.confidenceInterval ?? [],
        historicMonthsToDisplay: result?.historicMonthsToDisplay ?? [],
        predictionsToUse: result?.predictionsToUse ?? [],
        xLabels: result?.xLabels,
        monthColumns: result?.monthColumns,
        lastRealizedValue: result?.lastRealizedValue,
        realized: result?.realizedValue
      };
    },

    enabled: Boolean(productId) && shouldFetchPredictionByProduct,

    staleTime: CACHE_TTL_MS.NO_CACHE,
    cacheTime: CACHE_TTL_MS.NO_CACHE,

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,

    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },

    onError: (error) => {
      console.error("Erro ao carregar predições:", error);
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = "/";
      }
    },
  });

  const fetchLastSixMonthsPredictions = useQuery({
    queryKey: ["last6MonthsPredictions"],
    queryFn: async () => {
      
      const result = await getLastSixMonthsPredictions();
      
      return {
        forecastAssertivenessMonths: result.forecastsUsed,
        predictionsForAssertivenessAnalysis: result.predictions
      };
    },

    enabled: shouldFetchLastSixMonthsPredictions,
    staleTime: CACHE_TTL_MS.TTL_6_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    
    refetchOnMount: false,    


    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
    
    onError: (error) => {
      console.error("Erro ao carregar predições:", error);
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = "/";
      }
    },
  });

  const fetchConsolidatedIndexes = useQuery({
    queryKey: ["consolidatedIndexes"],

    queryFn: async () => {
      try {
        const result = await getConsolidatedIndexes();
        return result;
      } catch(err) {
        throw err;
      }
    },

    enabled: shouldFetchConsolidatedIndexes,
    staleTime: CACHE_TTL_MS.TTL_6_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    refetchOnMount: false,
  });

  const fetchConsolidatedPredictions = useQuery({
    queryKey: ["consolidated_predictions"],

    queryFn: async () => {
      try {
        const result = await getConsolidatedPredictions();
        return result;
      } catch(err) {
        throw err;
      }
    },

    enabled: shouldFetchConsolidatedPredictions,
    staleTime: CACHE_TTL_MS.TTL_6_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    refetchOnMount: false,
  });  

  return {
    productPredictionsData: {
      ...productPredictionsData,
      isLoadingProductPredictions: productPredictionsData.isLoading,
    },
    fetchLastSixMonthsPredictions: {
      ...fetchLastSixMonthsPredictions,
      isLoading: fetchLastSixMonthsPredictions.isLoading
    },
    indexes: {
      ...fetchConsolidatedIndexes,
      isLoading: fetchConsolidatedIndexes.isLoading
    },
    consolidated: {
      ...fetchConsolidatedPredictions,
      isLoadingConsolidatedPredictions: fetchConsolidatedPredictions.isLoading
    }
  };
};